import React, { useEffect, useRef, useState } from 'react';

import {
  buscarDetalheDaRecomendacao,
  RadarDetalhe,
} from '../../../../api/contac/contacRecomendacoesApi';
import ModalPlacaDre from '../../../../components/modal/contac/placa-dre/ModalPlacaDre';
import { formatarCelula } from '../../../../components/radar/formatarCelula';
import { notify } from '../../../../shared/feedback/notify';
import '../../../../shared/feedback/erro-de-tela.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface Props {
  detectorId: string;
  titulo: string;
  onClose: () => void;
}

/**
 * A tabela por trás de um card ("clique aqui para ver quais").
 *
 * Fecha por clique fora e por `Escape`. Carrega na abertura — e é por isso que a rota precisa
 * estar registrada no msw dos testes que abrem este modal.
 */
const DetalheRecomendacaoModal: React.FC<Props> = ({ detectorId, titulo, onClose }) => {
  const [detalhe, setDetalhe] = useState<RadarDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [placaAberta, setPlacaAberta] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const dados = await buscarDetalheDaRecomendacao(detectorId);
        if (ativo) setDetalhe(dados);
      } catch (error) {
        if (!ativo) return;
        // Estado seguro + sinal: sem o aviso, a tabela vazia seria lida como "a lista está vazia",
        // que aqui contradiz o número que o usuário acabou de clicar.
        setDetalhe(null);
        setErro(true);
        notify.errorFrom(
          error,
          'Não foi possível carregar os itens desta recomendação. Recarregue a página; se persistir, acione a TI.',
        );
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [detectorId]);

  useEffect(() => {
    // ⚠️ Com a DRE aberta por cima, os dois listeners viveriam em `document` e o clique/Esc
    // dentro dela fecharia ESTE modal por baixo — o clique na DRE é "fora" deste `ref`. A guarda
    // é a mesma que o ChecklistModal usa para o drawer de ajuda: enquanto o filho está aberto,
    // o pai não reage.
    if (placaAberta) return;

    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onClose();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose, placaAberta]);

  return (
    <div className="modal-overlay">
      <div className="do-modal do-modal-largo" ref={modalRef}>
        <div className="do-modal-header">
          <span className="do-modal-titulo">{titulo}</span>
          <button type="button" className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          {carregando && <p className="do-kpi-carregando">Carregando os itens...</p>}

          {!carregando && erro && (
            <p className="erro-de-tela">
              Não foi possível carregar os itens desta recomendação. O número exibido no card
              continua válido; o que falhou foi a consulta desta lista.
            </p>
          )}

          {!carregando && !erro && detalhe && (
            <>
              <p className="do-modal-apoio">
                Competências analisadas: {detalhe.competenciaInicio} a {detalhe.competenciaBase}.
                {' '}
                {detalhe.itens.length}{' '}
                {detalhe.itens.length === 1 ? 'item encontrado' : 'itens encontrados'}.
              </p>

              {detalhe.itens.length === 0 ? (
                <p className="do-modal-apoio">Nenhum item nesta recomendação no momento.</p>
              ) : (
                <div className="do-tabela-wrapper">
                  <table className="do-tabela">
                    <thead>
                      <tr>
                        {detalhe.colunas.map((coluna) => (
                          <th key={coluna.chave}>{coluna.rotulo}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detalhe.itens.map((item, indice) => (
                        <tr key={indice}>
                          {detalhe.colunas.map((coluna) => {
                            const valor = formatarCelula(item[coluna.chave], coluna);

                            // Só a coluna que o detector declarou como `placa` é acionável — a
                            // tabela continua genérica, sem lista de detectores no frontend.
                            if (coluna.tipo === 'placa' && valor !== '-') {
                              return (
                                <td key={coluna.chave} title={`Ver o detalhamento mensal de ${valor}`}>
                                  <button
                                    type="button"
                                    className="do-celula-link"
                                    onClick={() => setPlacaAberta(valor)}
                                  >
                                    {valor}
                                  </button>
                                </td>
                              );
                            }

                            return (
                              // Uma linha por célula, com reticências e o valor inteiro no title.
                              <td key={coluna.chave} title={valor}>
                                {valor}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detalhamento mensal da placa + os eventos do período, por cima desta tabela. */}
      {placaAberta && detalhe && (
        <ModalPlacaDre
          placa={placaAberta}
          dataInicio={primeiroDiaDaCompetencia(detalhe.competenciaInicio)}
          dataFim={ultimoDiaDaCompetencia(detalhe.competenciaBase)}
          isOpen
          onClose={() => setPlacaAberta(null)}
          mostrarEventos
        />
      )}
    </div>
  );
};

/** `MM/YYYY` → `YYYY-MM-01`, que é o formato que a API da DRE espera. */
function primeiroDiaDaCompetencia(competencia: string): string {
  const [mes, ano] = competencia.split('/');
  return `${ano}-${mes}-01`;
}

/** `MM/YYYY` → último dia daquele mês, em ISO. `0` no dia devolve o último dia do mês anterior. */
function ultimoDiaDaCompetencia(competencia: string): string {
  const [mes, ano] = competencia.split('/');
  const ultimo = new Date(Date.UTC(Number(ano), Number(mes), 0)).getUTCDate();
  return `${ano}-${mes}-${String(ultimo).padStart(2, '0')}`;
}

/*
 * `formatarCelula` morava aqui e era importada por três consumidores — inclusive pelo modal do
 * Relacionamento, que vive em outra view e nada tem a ver com este modal. Na transversal/at-82 ela
 * virou `components/radar/formatarCelula.ts`.
 */

export default DetalheRecomendacaoModal;
